import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Input from "antd/lib/input";
import Button from "antd/lib/button";
import Tooltip from "antd/lib/tooltip";
import Popover from "antd/lib/popover";
import Alert from "antd/lib/alert";
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import "./FormulaParameterInput.less";

const { TextArea } = Input;

function FormulaParameterInput({ value, onChange, className }) {
  const [formulaValue, setFormulaValue] = useState(value || "");
  const [validationStatus, setValidationStatus] = useState(null); // null, 'validating', 'success', 'error'
  const [validationError, setValidationError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [functions, setFunctions] = useState([]);
  const [variables, setVariables] = useState([]);

  // 加载函数和变量列表
  useEffect(() => {
    fetch("/api/formulas/functions")
      .then((res) => res.json())
      .then((data) => {
        if (data.functions) {
          setFunctions(data.functions);
        }
      })
      .catch((err) => console.error("Failed to load functions:", err));

    fetch("/api/formulas/variables")
      .then((res) => res.json())
      .then((data) => {
        if (data.variables) {
          setVariables(data.variables);
        }
      })
      .catch((err) => console.error("Failed to load variables:", err));
  }, []);

  // 手动验证公式（点击 Validate 按钮时触发）
  const validateFormula = () => {
    if (!formulaValue || formulaValue.trim() === "") {
      setValidationStatus(null);
      setValidationError(null);
      return;
    }

    setValidationStatus("validating");

    fetch("/api/formulas/validate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expression: formulaValue }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.valid) {
          setValidationStatus("success");
          setValidationError(null);
        } else {
          setValidationStatus("error");
          setValidationError(data.error || "Invalid formula");
        }
      })
      .catch((err) => {
        setValidationStatus("error");
        setValidationError("Failed to validate formula");
        console.error("Validation error:", err);
      });
  };

  // 测试公式
  const testFormula = () => {
    if (!formulaValue || formulaValue.trim() === "") {
      return;
    }

    fetch("/api/formulas/test", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expression: formulaValue }),
    })
      .then((res) => res.json())
      .then((data) => {
        setTestResult(data);
        if (data.success) {
          setValidationStatus("success");
          setValidationError(null);
        } else {
          setValidationStatus("error");
          setValidationError(data.error);
        }
      })
      .catch((err) => {
        setTestResult({ success: false, error: "Failed to test formula" });
        console.error("Test error:", err);
      });
  };

  // 处理输入变化（不自动验证）
  const handleChange = (e) => {
    const newValue = e.target.value;
    setFormulaValue(newValue);
    onChange(newValue);
    // 清除之前的验证状态和测试结果
    setValidationStatus(null);
    setValidationError(null);
    setTestResult(null);
  };

  // 插入函数
  const insertFunction = (funcName) => {
    const newValue = formulaValue + funcName + "()";
    setFormulaValue(newValue);
    onChange(newValue);
  };

  // 插入变量
  const insertVariable = (varName) => {
    const newValue = formulaValue + "$" + varName;
    setFormulaValue(newValue);
    onChange(newValue);
  };

  // 渲染验证状态图标
  const renderValidationIcon = () => {
    if (validationStatus === "validating") {
      return <span className="formula-validation-icon validating" role="img" aria-label="validating">...</span>;
    } else if (validationStatus === "success") {
      return (
        <Tooltip title="Formula is valid">
          <CheckCircleOutlined className="formula-validation-icon success" />
        </Tooltip>
      );
    } else if (validationStatus === "error") {
      return (
        <Tooltip title={validationError}>
          <CloseCircleOutlined className="formula-validation-icon error" />
        </Tooltip>
      );
    }
    return null;
  };

  // 渲染帮助内容
  const renderHelpContent = () => {
    const functionsByCategory = functions.reduce((acc, func) => {
      if (!acc[func.category]) {
        acc[func.category] = [];
      }
      acc[func.category].push(func);
      return acc;
    }, {});

    const variablesByCategory = variables.reduce((acc, variable) => {
      if (!acc[variable.category]) {
        acc[variable.category] = [];
      }
      acc[variable.category].push(variable);
      return acc;
    }, {});

    const categoryNames = {
      date_time: "Date & Time",
      math: "Math",
      text: "Text",
      logic: "Logic",
      array: "Array",
      user: "User",
      organization: "Organization",
      time: "Time",
    };

    return (
      <div className="formula-help-content">
        <div className="formula-help-section">
          <h4>Variables</h4>
          {Object.entries(variablesByCategory).map(([category, vars]) => (
            <div key={category} className="formula-category">
              <div className="formula-category-title">{categoryNames[category] || category}</div>
              <div className="formula-items">
                {vars.map((variable) => (
                  <div key={variable.name} className="formula-item">
                    <Button
                      size="small"
                      type="link"
                      onClick={() => insertVariable(variable.name)}
                      className="formula-item-button"
                    >
                      ${variable.name}
                    </Button>
                    <Tooltip title={variable.description}>
                      <InfoCircleOutlined className="formula-item-info" />
                    </Tooltip>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="formula-help-section">
          <h4>Functions</h4>
          {Object.entries(functionsByCategory).map(([category, funcs]) => (
            <div key={category} className="formula-category">
              <div className="formula-category-title">{categoryNames[category] || category}</div>
              <div className="formula-items">
                {funcs.map((func) => (
                  <div key={func.name} className="formula-item">
                    <Button
                      size="small"
                      type="link"
                      onClick={() => insertFunction(func.name)}
                      className="formula-item-button"
                    >
                      {func.signature}
                    </Button>
                    <Tooltip title={func.description}>
                      <InfoCircleOutlined className="formula-item-info" />
                    </Tooltip>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="formula-help-section">
          <h4>Examples</h4>
          <div className="formula-examples">
            <div className="formula-example">
              <code>$USER_EMAIL</code>
              <span>Current user email (direct value)</span>
            </div>
            <div className="formula-example">
              <code>$USERNAME</code>
              <span>Current user name (direct value)</span>
            </div>
            <div className="formula-example">
              <code>=&quot;&apos;&quot; + $USERNAME + &quot;&apos;&quot;</code>
              <span>User name with SQL quotes</span>
            </div>
            <div className="formula-example">
              <code>=QUERY(123)</code>
              <span>First value from query #123</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`formula-parameter-input ${className}`}>
      <div className="formula-input-wrapper">
        <textarea
          value={formulaValue}
          onChange={handleChange}
          placeholder="$USER_EMAIL or =IF($USER_ID > 100, 'a', 'b')"
          className="formula-textarea ant-input"
          rows={1}
        />
        <div className="formula-input-actions">
          {renderValidationIcon()}
          <Popover
            content={renderHelpContent()}
            title="Formula Reference"
            trigger="click"
            visible={showHelp}
            onVisibleChange={setShowHelp}
            placement="bottomRight"
            overlayClassName="formula-help-popover"
          >
            <Button size="small" icon={<InfoCircleOutlined />}>
              Help
            </Button>
          </Popover>
          <Button size="small" onClick={testFormula} type="primary">
            Test
          </Button>
        </div>
      </div>

      {validationStatus === "error" && validationError && (
        <Alert message={validationError} type="error" showIcon className="formula-error-alert" />
      )}

      {testResult && testResult.success && (
        <div className="formula-test-result">
          <Alert
            message={
              <div>
                <strong>Result:</strong> {JSON.stringify(testResult.result)}{" "}
                <span className="formula-result-type">({testResult.result_type})</span>
              </div>
            }
            type="success"
            showIcon
          />
        </div>
      )}
    </div>
  );
}

FormulaParameterInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};

FormulaParameterInput.defaultProps = {
  value: "",
  className: "",
};

export default FormulaParameterInput;
